<route lang="json">
{ "name": "opportunities-signup" }
</route>

<template>
  <div class="flex-row enrollment-area">
    <ContentEditorInsert v-model="content" />

    <div class="panels flex-stretch">
      <StarterKitPanel :content="content" />
      <SponsorPanel :content="content" />
      <AccountFormPanel :content="content" />
      <CheckoutPanel :content="content" />
    </div>

    <div class="summary-column">
      <div class="floater">
        <h3>Order Summary</h3>
        <div class="grid summary">
          <div>Subtotal</div>
          <div class="text-right">
            <Currency :amount="subtotal" />
          </div>
          <!-- <div>[TOTALS]</div>
        <div>{{ join.totals }}</div>
        <template v-if="join.totals?.items" v-for="item of join.totals?.items">
          <div>{{ item.ProductID }}</div>
          <div>{{ item.Price }}</div>
        </template> -->
          <template v-if="join.totals">
            <div>Tax</div>
            <div class="text-right">
              <Currency :amount="join.totals.TaxTotal" />
            </div>
            <div>Shipping</div>
            <div class="text-right">
              <Currency :amount="join.totals.ShippingTotal" />
            </div>

            <!-- <div>
              {{ join.kit.jewelry_count }} Free Items ({{ item.ProductID }})
            </div>
            <div class="text-right"></div> -->
          </template>
          <template v-else>
            <div>Tax</div>
            <div class="text-right">TBD</div>
            <div>Shipping</div>
            <div class="text-right">TBD</div>
          </template>
          <div>Additional Items</div>
          <div class="text-right">
            {{ join.extraItemCount }}
          </div>
          <div>Total Items</div>
          <div class="text-right">{{ join.count }}</div>
          <div class="total">Total</div>
          <div class="total text-right">
            <Currency
              v-if="join.totals?.BalanceDue"
              :amount="join.totals.BalanceDue"
            />
            <span v-else>TBD</span>
          </div>
        </div>
        <div class="flex-column flex-center m-sm-t">
          <div v-for="(errors, key) in join.errors">
            <Message severity="error">
              <div v-if="key !== '__all__'">
                <b>{{ key }}</b>
              </div>
              {{ errors.join(", ") }}
            </Message>
          </div>

          <div class="flex-row flex-center">
            <div
              v-if="(join.kit?.jewelry_count || 0) > join.count"
              class="flex-column gap-2xs"
            >
              <router-link :to="{ name: 'shop' }">
                <Button class="grey">Choose Jewelry</Button>
              </router-link>
              <div class="text-center">
                {{ join.count }} /
                {{ join.kit?.jewelry_count ?? 0 }}
              </div>
            </div>
            <Button
              v-else-if="!join.totals"
              @click="getTotals()"
              :class="{
                grey: !readyForTotals,
                pink: readyForTotals,
              }"
            >
              Checkout
            </Button>
            <Button
              v-else-if="!status"
              @click="checkout()"
              :disabled="!ready"
              :class="{
                grey: !ready,
                pink: ready,
              }"
            >
              Submit Order
            </Button>
            <Button v-else :disabled="true" class="grey">
              Processing...
            </Button>
          </div>
          <DebugBlock>
            <Button @click="fakeCheckout()"> Submit Fake </Button>
          </DebugBlock>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import ContentEditorInsert from "@/components/ContentEditorInsert.vue";
import DebugBlock from "@/components/DebugBlock.vue";
import AccountFormPanel from "@/components/join/AccountFormPanel.vue";
import CheckoutPanel from "@/components/join/CheckoutPanel.vue";
import SponsorPanel from "@/components/join/SponsorPanel.vue";
import StarterKitPanel from "@/components/join/StarterKitPanel.vue";
import Currency from "@/components/shop/Currency.vue";
import { useAddressStore } from "@/stores/address";
import { useCartStore } from "@/stores/cart";
import { useContentStore } from "@/stores/content";
import { useJoinStore } from "@/stores/join";
import { useOrderStore, type OrderWithId } from "@/stores/orders";
import { useUserStore } from "@/stores/user";
import { getConsultant } from "@/utils/reps";
import type { CartItem } from "@paparazzi/types/paparazzi.api.serializers";
import { timer } from "@paparazzi/utils/promise";
import { useHead } from "@unhead/vue";
import Button from "primevue/button";
import Message from "primevue/message";
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";

const cart = useCartStore();
const join = useJoinStore();
const user = useUserStore();
const contentStore = useContentStore();
const router = useRouter();
const orders = useOrderStore();
const addresses = useAddressStore();

const status = ref("");
const isTester = ref(localStorage.tester);
const content = ref({
  starter_kit_unpicked: "My Enrollment Option",
  starter_kit_picked: "Select an enrollment option",
  starter_kit_prompt: "Select one of the enrollment options below:",
  sponsor_unpicked: "Select a sponsor",
  sponsor_picked: "My Sponsor",
  account_header: "My Account Information",
  checkout_header: "Checkout",
});

const readyForTotals = computed(() => {
  return join.info.account && join.flagData.terms_agreed;
});

const ready = computed(() => {
  return (
    join.totals &&
    join.card.card &&
    join.cvc &&
    join.cvc.length >= 3 &&
    join.card.billing_address &&
    join.card.billing_address.zip_code
  );
});

const subtotal = computed(() => {
  const extraPrice = join.extraItems.reduce((a, b: CartItem) => {
    const price = b.product?.prices?.wholesale || 0;
    return a + price * b.quantity;
  }, 0);
  const kitPrice = parseFloat(join.kit?.unit_price || "0");
  return kitPrice + extraPrice;
});

useHead({
  title: "Join",
});

onMounted(async () => {
  join.getStarterKits();
  contentStore.update();

  addresses.allowPOBoxes = true;

  // join with query string
  await timer(100); // wait for `persist` to finish deserialization
  const consultantId =
    router.currentRoute.value.query.consultant || user.props.customer_rep;
  if (consultantId) {
    join.info.rep = await getConsultant(`${consultantId}`);
  }
});

async function getTotals() {
  if (!(await join.isReady()) || !(await join.check())) {
    alert("Please check your form for errors and try again");
  } else {
    user.startLogrocket("new-signup");
  }
}

async function checkout() {
  if (!(await join.isReady())) {
    alert("Please check your form for errors and try again");
  } else {
    // check cardform
    const form = document.querySelector("#cardForm") as
      | HTMLFormElement
      | undefined;
    if (form) {
      if (!form.reportValidity()) {
        return;
      }
    }

    // ensure billing
    if (!join.card.billing_address || !join.card.billing_address.zip_code) {
      join.errors = { __all__: ["Please choose a billing address"] };
      return;
    }

    status.value = "Processing...";
    const result = await join.start();
    if (result.consultant) {
      await resolveCheckout(result);
    }
    status.value = "";
  }
}

async function fakeCheckout() {
  status.value = "Processing...";
  join.passwordData.back_office_password = "Choccy milk";
  await resolveCheckout({
    consultant: 1130062,
    order: 33107515,
  });
  status.value = "";
}

async function resolveCheckout(result: { consultant: number; order: number }) {
  const password = join.passwordData.back_office_password;
  const order: OrderWithId = {
    id: result.order,
    created: new Date().toISOString(),
    shipping_address_text: `${join.addressData.address}
${join.addressData.address2}${join.addressData.address2 ? "\n" : ""}${join.addressData.city} ${join.addressData.state} ${join.addressData.zip_code}
${join.addressData.county}`,
  };
  if (user.props.token) {
    await user.logout({ reload: false });
  }
  await user.login(`${result.consultant}`, password);
  if (orders.store) {
    await orders.store.setItem(`${order.id}`, order);
  }
  router.push({
    name: "opportunities-success",
    params: { joinId: result.order },
  });

  join.clear();
}
</script>

<style scoped>
.flex-row.page {
  gap: var(--lg, 48px);
}

.enrollment-area {
  font-size: 14px;
  max-width: 100%;
}

#app :deep(.enrollment-terms .terms),
#app :deep(.enrollment-terms .checkbox) {
  min-width: 100px;
  max-width: 80vw;
  flex: 1 1 auto;
  white-space: wrap !important;
}

.panels {
  max-width: 100%;
}
.panel {
  padding: var(--1XL, 64px) 0;
  font-size: 14px;
}
.panel,
.panel1 {
  max-width: 810px;
  :deep(h2) {
    font-size: 18px;
    text-transform: uppercase;
    margin-bottom: var(--Sm, 24px);
    color: var(--Black, #000);
  }
}

#app .panel :deep(h2),
#app .panel1 :deep(h2) {
  font-size: 18px;
}
.panel1 {
  padding: 0 0 var(--1XL, 64px) 0;
  border-bottom: 1px solid var(--30, #c4c4c4);
  :deep(h2) {
    margin-bottom: var(--XS, 16px);
  }
}

:deep(.dot) {
  background-color: var(--PaparazziPink, #ec008c);
  width: 23px;
  height: 23px;
  border-radius: 50%;
  color: #fff;
  padding: 3px;
  text-align: center;
  font-weight: 500;
  font-size: 14px;
}

.disabled {
  pointer-events: none;
}

.panel.consultant {
  font-size: 14px;
  border-bottom: 1px solid var(--30, #c4c4c4);
  .flex-column {
    justify-content: center;
    gap: var(--2XS, 8px);
  }
}
.rep-search:deep(p) {
  margin: 0 !important;
}
.consultant-image {
  max-width: 100px;
  margin-right: var(--md, 32px);
}

.summary-column {
  position: sticky;
  top: 70px;
  width: 260px;
  height: auto;
  place-self: flex-start;
}

h3 {
  font-size: 14px;
  font-weight: 300;
  text-transform: uppercase;
  margin: 0 0 var(--3XS, 4px) 0;
}
.grid {
  padding: var(--XS);
  border: 1px solid var(--30, #c4c4c4);
  display: grid;
  grid-template-columns: auto auto;
  font-size: 14px;
  line-height: 1.5;
  & * {
    /* padding: 8px 0; */
  }
  .total {
    margin-top: var(--XS, 16px);
    border-top: 1px solid var(--Gray-30, #bcbec0);
    padding-top: var(--XS, 16px);
  }
}

@media (max-width: 1168px) and (min-width: 801px) {
  .flex-row.page {
    padding: 0 var(--Sm, 24px) !important;
  }
}
@media (max-width: 800px) {
  .summary-column {
    position: fixed;
    bottom: 0;
    top: auto;
    background: white;
    left: 0;
    right: 0;
    padding: 5px;
    z-index: 1;
  }
}
@media only screen and (max-width: 800px) {
  .flex-row,
  #app .flex-row {
    display: inline-grid;
    width: 100%;
  }
  .cart-items-cont {
    margin: 0;
    /* padding: 0 var(--Sm, 24px); */
    padding: 0;
  }
  .summary-column {
    position: static;
  }
  .summary-column > * {
    width: 100%;
  }
  .first {
    padding-left: var(--md, 32px);
  }
  .second {
    margin: var(--md, 32px) 0 0 var(--md, 32px);
    padding: 0;
  }
  .price-cell.paragraph1 {
    padding: 0px var(--2XS, 8px);
  }

  .image-cell {
    width: 100%;
    grid-column: 1/1;
    grid-row: span 3;
    border-bottom: none;
    margin: var(--md, 32px) 0 0 0;
  }

  .meta-cell {
    gap: var(--2XS);
    border-bottom: none;
  }
  .meta-cell.flex-space-between {
    justify-content: normal;
  }

  .quantity-cell {
    padding: 0;
    margin: 0;
    border-bottom: 0;
    grid-column: span 2;
  }
  .quantity-cell .flex-row {
    border: 1px solid var(--30, #c4c4c4);
    padding: 0px;
    display: flex !important;
    gap: 0;
    margin: 0 0 var(--Sm, 24px) 0;
    min-width: 92px !important;
  }
  .quantity-cell .flex-row :deep(.p-button-icon) {
    width: 7px;
    height: 7px;
  }

  .p-button.small {
    padding: 0;
  }

  .price-cell {
    border-bottom: 0;
  }

  .summary-column {
    width: 80%;
    margin: 0 auto;
  }
  .price-cell {
    grid-column: 3/3;
  }
  .quantity-cell {
    grid-column: 2/4;
  }
  #app .total-cell {
    padding: 5px 0 var(--md, 32px) 0;
    margin: 0 0 0 var(--md, 32px);
    grid-column: span 2;
  }
  #app .total-cell .price {
    display: none;
  }
  #app .quantity-cell {
    width: 94px;
    padding: var(--Sm, 24px) 0 0 var(--md, 32px);
  }
  #shopping-button {
    margin-top: var(--Sm, 24px);
  }
}

@media screen and (max-width: 600px) {
  .summary-column {
    width: 100%;
  }
  .header-total {
    text-align: right;
  }
  .price-cell.paragraph1 {
    text-align: right;
  }
}
</style>
